/**
 * F18 — Shared types for the customer onboarding wizard.
 *
 * Pure module — no React, no Convex. Imported by both the wizard
 * components and by `src/lib/nipe/tools/*` indirectly via the global
 * `window.__repancyOnboardWizard` bridge that `new.tsx` registers on
 * mount.
 */

export type OnboardStep =
  | 'intro'
  | 'tenant'
  | 'consent'
  | 'signin'
  | 'firm'
  | 'byo'
  | 'provisioning'
  | 'completed';

export interface OnboardFormState {
  tenantDomain: string;
  tenantId: string | null;
  tenantDisplayName: string | null;
  consentGrantedAt: string | null;
  firmName: string;
  slug: string;
  practiceArea: string;
  theme: string;
  primaryColor: string;
}

export const EMPTY_FORM_STATE: OnboardFormState = {
  tenantDomain: '',
  tenantId: null,
  tenantDisplayName: null,
  consentGrantedAt: null,
  firmName: '',
  slug: '',
  practiceArea: '',
  theme: '',
  // Deep red default per operator direction 2026-05-27. Single
  // color (not a gradient) because --portal-accent consumers use
  // this value in `borderColor` / `color` / `outline` / `box-shadow`
  // contexts which don't accept gradient strings. Gradient default
  // would require splitting into `primaryColor` + `primaryGradient`
  // with consumers branching — deferred until requested.
  primaryColor: '#8b0000',
};

export const PRACTICE_AREAS = [
  'corporate-ma',
  'litigation-disputes',
  'trusts-estates',
  'employment',
  'ip',
  'other',
] as const;

export const THEMES = ['xp-luna', 'win95', 'modern'] as const;

/**
 * Global window-level bridge for NIPE → wizard control. The wizard
 * route registers itself on mount and clears the registry on unmount.
 * NIPE tools at `src/lib/nipe/tools/*` check `window.__repancyOnboardWizard`
 * to decide whether the wizard is reachable.
 *
 * Per Round 1 hard limit: `kickAdminConsent` returns a `consentUrl` for
 * the USER to click; this bridge never calls `window.location.href = ...`.
 */
export interface OnboardWizardBridge {
  currentStep: () => OnboardStep;
  goToStep: (step: OnboardStep) => Promise<{ ok: boolean; currentStep: OnboardStep }>;
  setField: (
    name: keyof OnboardFormState | 'tenantDomain' | 'firmName' | 'slug' | 'practiceArea' | 'theme' | 'primaryColor',
    value: string,
  ) => Promise<{ ok: boolean; previousValue: string | null }>;
  getState: () => OnboardFormState;
  submitCurrentStep: () => Promise<
    | { kind: 'submitted'; nextStep: OnboardStep }
    | { kind: 'queued-for-approval'; pendingWriteId: string }
    | { kind: 'error'; message: string }
  >;
}

declare global {
  interface Window {
    __repancyOnboardWizard?: OnboardWizardBridge;
  }
}
